import React, { useEffect, useMemo, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import confetti from "canvas-confetti";
import { UnoCardBack } from "@/components/UnoCard";

/**
 * Full-screen deal animation. Renders cards flying from a central deck to
 * each seated player. Used at game start when status transitions
 * "waiting" -> "playing".
 *
 * props:
 *  - active: boolean, shows/hides the overlay
 *  - players: array of player objects. `me` is placed at bottom center.
 *  - myUserId: string, current viewer's user_id
 *  - onDone: () => void, called when animation completes
 *  - cardsPerPlayer: number, how many cards to fly per player
 *  - variant: "uno" | "skipbo" - controls colors slightly
 */
export default function DealAnimation({ active, players = [], myUserId, onDone, cardsPerPlayer = 7, variant = "uno" }) {
  // Determine on-screen target for each player (relative to viewport center)
  const targets = useMemo(() => {
    const nonMe = players.filter(p => p.user_id !== myUserId);
    const list = [];
    // Bottom-center for me
    if (players.some(p => p.user_id === myUserId)) list.push({ user_id: myUserId, x: 0, y: 240, rot: 0 });
    // Fan opponents across the top
    if (nonMe.length === 1) list.push({ user_id: nonMe[0].user_id, x: 0, y: -220, rot: 180 });
    else if (nonMe.length === 2) {
      list.push({ user_id: nonMe[0].user_id, x: -320, y: -160, rot: 150 });
      list.push({ user_id: nonMe[1].user_id, x: 320, y: -160, rot: 210 });
    } else {
      const span = 700;
      nonMe.forEach((p, i) => {
        const t = nonMe.length === 1 ? 0.5 : i / (nonMe.length - 1);
        const x = -span/2 + t * span;
        list.push({ user_id: p.user_id, x, y: -180, rot: 180 + (t - 0.5) * 40 });
      });
    }
    return list;
  }, [players, myUserId]);

  useEffect(() => {
    if (!active) return;
    // Total duration: cardsPerPlayer * targets.length * stagger + tail
    const stagger = 100;
    const per = 500;
    const total = cardsPerPlayer * targets.length * stagger + per + 400;
    const t = setTimeout(() => onDoneRef.current?.(), total);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, cardsPerPlayer, targets.length]);
  const onDoneRef = useRef(onDone);
  useEffect(() => { onDoneRef.current = onDone; }, [onDone]);

  const cards = useMemo(() => {
    if (!active) return [];
    const out = [];
    for (let round = 0; round < cardsPerPlayer; round++) {
      for (let i = 0; i < targets.length; i++) {
        out.push({
          id: `${round}-${i}`,
          target: targets[i],
          delay: (round * targets.length + i) * 100,
        });
      }
    }
    return out;
  }, [active, cardsPerPlayer, targets]);

  return (
    <AnimatePresence>
      {active && (
        <motion.div
          key="deal-overlay"
          initial={{ opacity: 1 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0, transition: { duration: 0.3 } }}
          className="fixed inset-0 z-50 pointer-events-none flex items-center justify-center"
          data-testid="deal-animation"
        >
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm"/>
          <div className="relative" style={{ perspective: 900 }}>
            {/* Deck stack pulse */}
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: [0.95, 1.05, 0.95] }}
              transition={{ duration: 1.2, repeat: Infinity }}
              className="relative"
            >
              <div className="absolute" style={{ top: 4, left: 4 }}><UnoCardBack size="lg" /></div>
              <div className="absolute" style={{ top: 2, left: 2 }}><UnoCardBack size="lg" /></div>
              <UnoCardBack size="lg" />
            </motion.div>
            <div className="text-center text-xs uppercase tracking-[0.3em] text-white/70 font-semibold mt-4">Dealing…</div>

            {cards.map(c => (
              <motion.div
                key={c.id}
                initial={{ x: 0, y: 0, rotate: 0, scale: 0.9, opacity: 0 }}
                animate={{ x: c.target.x, y: c.target.y, rotate: c.target.rot, scale: 0.75, opacity: [0, 1, 1, 0.9] }}
                transition={{ delay: c.delay / 1000, duration: 0.55, ease: [0.2, 0.9, 0.25, 1] }}
                className="absolute top-0 left-0"
                style={{ transformOrigin: "center" }}
              >
                <UnoCardBack size="sm" />
              </motion.div>
            ))}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/**
 * Full-screen victory overlay with confetti + winner reveal.
 * Fires confetti on mount and every 1.2s while visible.
 */
export function WinAnimation({ winnerName, winnerAvatar, isMe, subtitle, onContinue, primaryLabel = "Play again", onPrimary, canPrimary = true }) {
  useEffect(() => {
    const fire = () => {
      confetti({
        particleCount: isMe ? 140 : 80,
        spread: 90,
        startVelocity: 45,
        origin: { y: 0.35 },
        colors: ["#FF3366", "#FFCC00", "#00E676", "#00BFFF", "#B84CFF"],
      });
      confetti({
        particleCount: isMe ? 80 : 40,
        angle: 60,
        spread: 55,
        origin: { x: 0, y: 0.6 },
        colors: ["#FF3366", "#FFCC00", "#00E676", "#00BFFF"],
      });
      confetti({
        particleCount: isMe ? 80 : 40,
        angle: 120,
        spread: 55,
        origin: { x: 1, y: 0.6 },
        colors: ["#FF3366", "#FFCC00", "#00E676", "#B84CFF"],
      });
    };
    fire();
    const iv = setInterval(fire, isMe ? 1400 : 2500);
    return () => clearInterval(iv);
  }, [isMe]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="absolute inset-0 z-40 flex items-center justify-center bg-black/70 backdrop-blur-md"
      data-testid="win-animation"
    >
      <motion.div
        initial={{ scale: 0.7, y: 30, opacity: 0 }}
        animate={{ scale: 1, y: 0, opacity: 1 }}
        transition={{ type: "spring", stiffness: 220, damping: 18 }}
        className="glass rounded-3xl p-8 sm:p-10 text-center max-w-md border-2 border-[color:var(--uno-yellow)]"
      >
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1, rotate: [0, -8, 8, 0] }}
          transition={{ delay: 0.2, duration: 0.8, ease: "backOut" }}
          className="relative mx-auto"
          style={{ width: 88, height: 88 }}
        >
          <div className="absolute inset-0 rounded-full bg-[color:var(--uno-yellow)] blur-2xl opacity-70" />
          <img src={winnerAvatar} alt="" className="relative w-22 h-22 rounded-full border-4 border-[color:var(--uno-yellow)] shadow-2xl" style={{width: 88, height: 88}}/>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="text-xs uppercase tracking-[0.4em] text-[color:var(--uno-yellow)] mt-5 font-semibold"
        >
          {isMe ? "Victory" : "Winner"}
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.65 }}
          className="font-display text-4xl sm:text-5xl mt-2 tracking-tight"
        >
          {isMe ? "You won!" : winnerName}
        </motion.div>
        {subtitle && (
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.85 }}
            className="text-white/70 mt-3 text-sm"
          >{subtitle}</motion.p>
        )}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1 }}
          className="mt-6 flex gap-3 justify-center"
        >
          {canPrimary && (
            <button onClick={onPrimary} className="px-6 py-2.5 rounded-full bg-[color:var(--uno-green)] text-black font-semibold hover:brightness-110">{primaryLabel}</button>
          )}
          <button onClick={onContinue} className="px-6 py-2.5 rounded-full bg-white/10 hover:bg-white/20 text-white font-semibold">Lobby</button>
        </motion.div>
      </motion.div>
    </motion.div>
  );
}

/**
 * A single card flying from the central deck to a target position.
 * Used when a specific player draws a card in real time.
 */
export function FlyingCard({ targetX, targetY, onDone, duration = 0.55 }) {
  useEffect(() => {
    const t = setTimeout(() => onDone?.(), duration * 1000);
    return () => clearTimeout(t);
  }, [duration, onDone]);
  return (
    <motion.div
      initial={{ x: 0, y: 0, scale: 1, opacity: 0 }}
      animate={{ x: targetX, y: targetY, scale: 0.7, opacity: [0, 1, 1, 0.7] }}
      transition={{ duration, ease: [0.2, 0.9, 0.25, 1] }}
      className="absolute top-0 left-0 pointer-events-none z-30"
    >
      <UnoCardBack size="sm" />
    </motion.div>
  );
}


/**
 * Compute an on-screen offset (from viewport center) for each player, so we
 * can animate a played card flying FROM the player's tile TO the discard.
 */
function playerOriginOffset(players, playerUserId, myUserId) {
  const nonMe = players.filter(p => p.user_id !== myUserId);
  if (playerUserId === myUserId) return { x: 0, y: 260, rot: 0 };
  const idx = nonMe.findIndex(p => p.user_id === playerUserId);
  if (idx < 0) return { x: 0, y: -220, rot: 180 };
  if (nonMe.length === 1) return { x: 0, y: -220, rot: 180 };
  if (nonMe.length === 2) {
    return idx === 0
      ? { x: -320, y: -180, rot: -12 }
      : { x: 320, y: -180, rot: 12 };
  }
  const span = 700;
  const t = idx / (nonMe.length - 1);
  return { x: -span/2 + t * span, y: -190, rot: (t - 0.5) * 20 };
}

/**
 * Overlay for animating cards flying from a player -> the discard pile at
 * viewport center. Renders one card per active `plays` entry.
 *
 * props:
 *  - plays: array of { id, card, fromUserId, variant }
 *  - players, myUserId: to compute origin offsets
 *  - renderCard: function(card, variant) => JSX (falls back to UnoCard/back)
 *  - onFinished: (id) => void, called after a card lands so parent can dequeue
 */
export function PlayFlightLayer({ plays = [], players = [], myUserId, renderCard, onFinished }) {
  return (
    <div className="fixed inset-0 z-40 pointer-events-none flex items-center justify-center">
      {plays.map(p => {
        const origin = playerOriginOffset(players, p.fromUserId, myUserId);
        return (
          <FlyingPlayedCard
            key={p.id}
            id={p.id}
            origin={origin}
            card={p.card}
            variant={p.variant || "uno"}
            renderCard={renderCard}
            onDone={onFinished}
          />
        );
      })}
    </div>
  );
}

function FlyingPlayedCard({ id, origin, card, variant, renderCard, onDone }) {
  useEffect(() => {
    const t = setTimeout(() => onDone?.(id), 620);
    return () => clearTimeout(t);
  }, [id, onDone]);
  return (
    <motion.div
      initial={{ x: origin.x, y: origin.y, rotate: origin.rot, scale: 0.85, opacity: 0 }}
      animate={{ x: 0, y: 0, rotate: 0, scale: 1, opacity: [0, 1, 1, 0.9] }}
      transition={{ duration: 0.55, ease: [0.2, 0.85, 0.25, 1] }}
      className="absolute"
      style={{ transformOrigin: "center" }}
    >
      {renderCard ? renderCard(card, variant) : <UnoCardBack size="md" />}
    </motion.div>
  );
}
